import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

interface StaffConfigRowProps {
  icon: LucideIcon;
  title: string;
  /** Short explanation shown under the title. */
  hint?: string;
  /** Current value / summary, shown next to the actions. */
  children?: ReactNode;
  actions?: ReactNode;
}

/**
 * One staff-only setting inside the Staff tab settings list: icon tile,
 * title + hint on the left, current value and edit action on the right.
 * Rows are separated by the parent list (`divide-y`).
 */
export function StaffConfigRow({
  icon: Icon,
  title,
  hint,
  children,
  actions,
}: StaffConfigRowProps) {
  return (
    <div className="flex flex-col gap-3 py-3.5 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
      <div className="flex min-w-0 items-start gap-3">
        <span className="bg-primary-50 text-primary-600 ring-primary-600/15 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ring-1 ring-inset">
          <Icon
            className="h-4 w-4"
            aria-hidden
          />
        </span>
        <div className="min-w-0">
          <p className="text-surface-900 text-sm font-medium">{title}</p>
          {hint ? (
            <p className="text-surface-500 mt-0.5 text-[13px] leading-snug">
              {hint}
            </p>
          ) : null}
        </div>
      </div>
      {children || actions ? (
        <div className="flex min-w-0 shrink-0 flex-wrap items-center gap-x-3 gap-y-2 pl-11 sm:justify-end sm:pl-0">
          {children ? (
            <div className="flex min-w-0 items-center gap-2">{children}</div>
          ) : null}
          {actions ? (
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              {actions}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
